(() => {
  const T = (key, fallback, n) => (window.stlukesT || ((k, f, n) => f.replace('{n}', n)))(key, fallback, n);
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;

  const body = drawer.querySelector('[data-cart-body]');
  const totalEl = drawer.querySelector('[data-cart-total]');
  const checkout = drawer.querySelector('[data-cart-checkout]');
  const shipBar = drawer.querySelector('[data-ship-bar]');
  const shipText = drawer.querySelector('[data-ship-text]');
  const shipFill = drawer.querySelector('[data-ship-fill]');
  const threshold = Number(shipBar?.dataset.threshold || 10000);
  const pointsEl = drawer.querySelector('[data-cart-points]');
  const pointsRate = Number(drawer.dataset.pointsRate || 0);
  const rewardsUrl = drawer.dataset.rewardsUrl || '/pages/rewards';

  let lastFocus = null;
  let busy = false;

  // ---------- purchase caps ----------

  // How many of a product one order may hold, keyed by variant id.
  //
  // /cart.js carries no tags and no metafields, so the cap cannot be worked out
  // here. Three sources feed this instead, cheapest first: what Liquid knew at
  // render, what an add tells us, and a one-off lookup for anything still
  // unknown. The lookup only ever fires for a line this tab did not add.
  const limits = new Map();
  const limitNote = drawer.dataset.limitNote || '';

  const seed = drawer.querySelector('[data-cart-limits]');
  if (seed) {
    try {
      for (const [id, cap] of Object.entries(JSON.parse(seed.textContent))) {
        // null is a real answer here: this one is not capped. Stored so a
        // redraw does not mistake it for something still to look up.
        limits.set(String(id), cap === null ? null : Number(cap));
      }
    } catch (error) {
      // A malformed seed must not take the cart down with it. Every cap it
      // would have carried is still recoverable from the product endpoint.
      console.warn('cart limits seed ignored', error);
    }
  }

  const cappedTag = (drawer.dataset.limitTag || '').toLowerCase().trim();
  const cappedQty = Number(drawer.dataset.limitQty || 0);

  // A product fetched from /products/<handle>.js reports its tags but knows
  // nothing of the theme's settings, so the tag list is matched against the
  // same tag Liquid used. This misses a per-product metafield override, which
  // is why it is the last resort rather than the first.
  const capFromTags = (tags) => {
    if (!cappedTag || !cappedQty || !Array.isArray(tags)) return null;
    return tags.some((t) => String(t).toLowerCase().trim() === cappedTag) ? cappedQty : null;
  };

  const pending = new Map();

  const learn = async (item) => {
    const key = String(item.variant_id);
    if (limits.has(key)) return limits.get(key);
    if (!item.handle) return null;

    if (!pending.has(item.handle)) {
      pending.set(
        item.handle,
        fetch(`${window.Shopify?.routes?.root || '/'}products/${item.handle}.js`, {
          headers: { Accept: 'application/json' }
        })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      );
    }

    const product = await pending.get(item.handle);
    const cap = product ? capFromTags(product.tags) : null;
    // Recorded either way, including "no cap", so this line is never looked up
    // twice. A failed fetch records no cap, which is the safe direction to fail
    // in a drawer: the checkout gate still catches it before the order goes.
    limits.set(key, cap);
    return cap;
  };

  const capFor = (item) => {
    const cap = limits.get(String(item.variant_id));
    return typeof cap === 'number' ? cap : null;
  };

  // Fills in anything the seed did not cover, then reports whether the picture
  // changed enough to be worth redrawing.
  const learnAll = async (cart) => {
    const unknown = cart.items.filter((item) => !limits.has(String(item.variant_id)));
    if (!unknown.length) return false;
    await Promise.all(unknown.map(learn));
    // Only worth a redraw if something came back capped. Learning that four
    // shampoos are uncapped changes nothing on screen.
    return unknown.some((item) => capFor(item) !== null);
  };

  const money = (cents) =>
    new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD' }).format(cents / 100);

  const thumb = (url, size = 192) =>
    url ? `${url}${url.includes('?') ? '&' : '?'}width=${size}` : '';

  const escape = (value) =>
    String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const renderShipping = (total) => {
    const remaining = threshold - total;
    if (remaining > 0) {
      shipBar.classList.remove('cartd__ship--met');
      // Short, because it now shares a row with the drawer title. The old
      // wording ran to two lines beside it on a 360px phone.
      shipText.innerHTML = T('to_free_delivery', '{n} to free delivery', `<strong>${escape(money(remaining))}</strong>`);
      shipFill.style.width = `${Math.min(100, (total / threshold) * 100)}%`;
    } else {
      shipBar.classList.add('cartd__ship--met');
      shipText.textContent = T('free_delivery_met', 'Free delivery unlocked');
      shipFill.style.width = '100%';
    }
  };

  const renderLine = (item) => {
    const isGift = Boolean(item.properties && item.properties._gift);
    const cap = capFor(item);
    const atCap = cap !== null && item.quantity >= cap;
    const variant =
      item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cartd__line-variant">${escape(item.variant_title)}</p>` : '';
    const compare =
      item.original_line_price > item.final_line_price
        ? `<s>${escape(money(item.original_line_price))}</s>`
        : '';

    // Properties prefixed with an underscore are the theme's own bookkeeping.
    // Everything else was typed by the shopper and belongs on screen.
    const notes = Object.entries(item.properties || {})
      .filter(([name, value]) => !name.startsWith('_') && value)
      .map(
        ([name, value]) =>
          `<p class="cartd__line-note"><span>${escape(name)}:</span> ${escape(String(value))}</p>`
      )
      .join('');

    // A discount Shopify has applied to this line, by name, so a shopper can see
    // a bundle's extra coming off rather than just a lower price.
    // The free gift already says what it is.
    const deals = isGift
      ? ''
      : (item.line_level_discount_allocations || [])
          .filter((allocation) => allocation.amount > 0 && allocation.discount_application?.title)
          .map(
            (allocation) =>
              `<p class="cartd__line-deal">${escape(allocation.discount_application.title)} &minus;${escape(money(allocation.amount))}</p>`
          )
          .join('');

    return `
      <div class="cartd__line" data-line-key="${escape(item.key)}" data-variant-id="${escape(item.variant_id)}">
        ${
          item.image
            ? `<img class="cartd__line-media" src="${escape(thumb(item.image))}" alt="" width="92" height="92" loading="lazy">`
            // Some of the catalogue has no photograph. An img with an empty src
            // draws the browser's broken-image icon, which reads as a fault.
            : '<span class="cartd__line-media cartd__line-media--none" aria-hidden="true"></span>'
        }
        <div>
          <a class="cartd__line-title" href="${escape(item.url)}">${escape(item.product_title)}</a>
          ${variant}
          ${notes}
          ${deals}
          ${isGift
            ? `<span class="cartd__gift">
                <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                  <path d="M2 7h12v7H2zM1 4.5h14V7H1zM8 4.5v9.5M8 4.5S6.8 1.5 5 1.5a1.5 1.5 0 0 0 0 3zM8 4.5s1.2-3 3-3a1.5 1.5 0 0 1 0 3z"
                    fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/>
                </svg>
                Free gift
              </span>`
            : ''}
          ${atCap ? `<p class="cartd__line-limit">Limit ${cap} per order</p>` : ''}
          <div class="cartd__line-row">
            <span class="cartd__qty">
              <button type="button" data-qty-down aria-label="Reduce quantity"${isGift ? ' disabled' : ''}>&minus;</button>
              <output aria-label="Quantity">${item.quantity}</output>
              <button type="button" data-qty-up aria-label="Increase quantity"${isGift || atCap ? ' disabled' : ''}>+</button>
            </span>
            <span class="cartd__line-price">${escape(money(item.final_line_price))}${compare}</span>
          </div>
        </div>
      </div>`;
  };

  // Bundle & save. Someone who adds one product from a bundle on its own is told
  // it comes in a bundle, with the rest a tap away. Which products make up which
  // bundle is read from the Bundle & save page itself (the data it carries in
  // script#bundles-data), once a visit, so it always matches the bundles set up
  // in the theme editor. No bundles, or no page: nothing shows.
  const BUNDLES_URL = `${window.Shopify?.routes?.root || '/'}pages/bundles`;
  let bundles = null;

  const loadBundles = async () => {
    if (bundles) return bundles;
    try {
      const kept = JSON.parse(sessionStorage.getItem('stlukesBundles.v3') || 'null');
      if (kept && Date.now() - kept.at < 10 * 60 * 1000) return (bundles = kept.list);
    } catch (storageError) {
      /* private mode: fetch instead */
    }
    try {
      const html = await (await fetch(BUNDLES_URL)).text();
      const found = html.match(/id="bundles-data">([\s\S]*?)<\/script>/);
      bundles = found ? JSON.parse(found[1]) : [];
    } catch (fetchError) {
      bundles = [];
    }
    try {
      sessionStorage.setItem('stlukesBundles.v3', JSON.stringify({ at: Date.now(), list: bundles }));
    } catch (storageError) {
      /* nothing to keep it in */
    }
    return bundles;
  };

  const drawBundleNudges = async (cart) => {
    const list = await loadBundles();
    if (!list.length || cart !== lastCart) return;
    for (const bundle of list) {
      const held = cart.items.filter((item) => bundle.items.some((b) => b.id === item.variant_id));
      if (!held.length) continue;
      // Already getting the extra (three of one product counts too): say nothing.
      if (held.some((item) => (item.line_level_discount_allocations || []).some((a) => a.amount > 0))) continue;
      const missing = bundle.items.filter((b) => !held.some((item) => item.variant_id === b.id));
      if (!missing.length || missing.some((b) => !b.available)) continue;

      const price = missing.reduce((sum, b) => sum + b.price, 0);
      const what = missing.length === 1 ? 'the last one' : `the other ${missing.length}`;
      const lastLine = body.querySelector(`.cartd__line[data-line-key="${CSS.escape(held[held.length - 1].key)}"]`);
      if (!lastLine) continue;
      lastLine.insertAdjacentHTML(
        'afterend',
        `<div class="cartd__bundle">
          <p>Part of our <a href="${escape(bundle.url)}">${escape(bundle.title)}</a> bundle. Add ${what} and take an extra ${escape(bundle.extra)}% off all ${bundle.items.length}.</p>
          <button type="button" data-bundle-add="${escape(missing.map((b) => b.id).join(','))}">Add ${what} &middot; ${escape(money(price))}</button>
        </div>`
      );
    }
  };

  // The cart as last drawn. Every write returns the whole cart and every draw
  // goes through here, so this is current without anyone fetching for it.
  let lastCart = null;

  const render = (cart) => {
    lastCart = cart;

    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });

    if (totalEl) totalEl.textContent = money(cart.total_price);
    if (checkout) checkout.disabled = cart.item_count === 0;

    body.innerHTML = cart.items.length
      ? cart.items.map(renderLine).join('')
      : '<p class="cartd__empty">Your cart is empty.</p>';

    if (cart.items.length) drawBundleNudges(cart);

    // Any line this tab has not seen before, which in practice means one added
    // in another tab. Drawn first without its cap so the drawer is never held
    // up by a lookup, then drawn again only if a cap turned up.
    learnAll(cart).then((learned) => {
      if (learned) render(cart);
    });

    renderShipping(cart.total_price);

    // Points belong to the order, so they are quoted against the cart total
    // rather than against a single product's price.
    if (pointsEl) {
      const earned = Math.floor((cart.total_price / 100) * pointsRate);
      pointsEl.innerHTML =
        `Earn <strong>${earned}</strong> reward points on this order` +
        `<a class="cartd__points-help" href="${escape(rewardsUrl)}" aria-label="How reward points work">?</a>`;
      pointsEl.hidden = pointsRate <= 0 || earned <= 0;
    }
  };

  const getCart = async () => {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' }
    });
    return response.json();
  };

  const refresh = async () => {
    const cart = await getCart();
    render(cart);
    return cart;
  };

  const open = () => {
    lastFocus = document.activeElement;
    drawer.hidden = false;
    document.body.style.overflow = 'hidden';
    drawer.querySelector('[data-cart-close]')?.focus();
  };

  const close = () => {
    drawer.hidden = true;
    document.body.style.overflow = '';
    lastFocus?.focus();
  };

  const post = async (path, payload) => {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      // Shopify explains the refusal in the body — "You can only add 2 of
      // this to your cart" — and throwing that away is what made a rejected
      // add look like nothing happening.
      let detail = '';
      try {
        const body = await response.json();
        detail = body.description || body.message || '';
      } catch (parseError) {
        /* not every failure carries a JSON body */
      }
      const error = new Error(detail || `${path} failed with ${response.status}`);
      error.status = response.status;
      error.detail = detail;
      throw error;
    }

    return response.json();
  };

  const add = async (variantId, quantity = 1, extras = [], cap = null) => {
    // The caller usually knows the cap, because it rendered the product: a
    // quick-add button carries it, and the product page hands over its own.
    // Recording it here is what keeps the drawer from having to look it up.
    if (cap !== null && cap !== undefined) limits.set(String(variantId), Number(cap));

    const known = limits.get(String(variantId));

    if (typeof known === 'number') {
      // Counted from the cart the drawer already holds rather than by fetching
      // it again. A fetch here would cost every medicine add a round trip, and
      // the only thing it would catch is a change made in another tab, which
      // the check before checkout catches anyway.
      const held = (lastCart?.items || [])
        .filter((item) => String(item.variant_id) === String(variantId))
        .reduce((sum, item) => sum + item.quantity, 0);

      const room = known - held;

      if (room <= 0) {
        open();
        say(limitNote || T('limit', 'We limit this to {n} per order.', known));
        return lastCart;
      }

      if (quantity > room) quantity = room;
    }

    say('');
    const items = [{ id: Number(variantId), quantity }, ...extras];
    await post('cart/add.js', { items });
    await refresh();
    open();
  };

  const setQuantity = async (key, quantity) => {
    // Returns the cart, because Shopify caps a line at the stock it has and
    // answers 200 with the capped number rather than refusing. Without reading
    // it back, pressing + at the ceiling looked like a dead button.
    //
    // Drawn from that response rather than followed by refresh(). change.js
    // hands back the whole cart, so fetching /cart.js again asked for something
    // already in hand and put a second round trip, about 225ms of the roughly
    // 1080ms a shopper waits, between the tap and the number changing.
    const cart = await post('cart/change.js', { id: key, quantity });
    render(cart);
    return cart;
  };

  const notice = drawer.querySelector('[data-cart-notice]');

  const say = (message) => {
    if (!notice) return;
    notice.textContent = message || '';
    notice.hidden = !message;
  };


  // Removes every line of a variant, whatever properties each line carries.
  // change.js addresses one line at a time and cannot do this.
  const clearVariant = async (variantId) => {
    // update.js returns the cart as well, so the drawer is brought up to date
    // from the answer instead of being left stale until something else fetches.
    const cart = await post('cart/update.js', { updates: { [String(variantId)]: 0 } });
    render(cart);
    return cart;
  };

  // ---------- delivery instructions ----------

  const noteField = drawer.querySelector('[data-cart-note]');
  const noteState = drawer.querySelector('[data-cart-note-state]');

  if (noteField) {
    let saveTimer = null;
    let lastSaved = noteField.value;

    // The markup opens the box only when a note is already written, which is
    // the state a phone needs: open, the label and a three row field pushed the
    // subtotal and the checkout button below the fold. A desktop drawer has the
    // room, so it opens there and the shopper sees the field without a tap.
    const wrap = drawer.querySelector('[data-cart-note-wrap]');
    const noteTag = drawer.querySelector('[data-cart-note-tag]');
    if (wrap && window.matchMedia('(min-width: 900px)').matches) wrap.open = true;

    const saveNote = async () => {
      const note = noteField.value;
      if (note === lastSaved) return;

      try {
        await post('cart/update.js', { note });
        lastSaved = note;
        if (noteState) noteState.textContent = note ? 'Saved' : '';
        // Closed again, the tag is the only thing saying a note is in there.
        if (noteTag) noteTag.hidden = note.trim() === '';
      } catch (error) {
        if (noteState) noteState.textContent = T('note_failed', 'Could not save that just now.');
      }
    };

    // Saved as they type, but not on every keystroke: one request once they
    // pause, and one more on blur in case they close the drawer mid-thought.
    noteField.addEventListener('input', () => {
      if (noteState) noteState.textContent = '';
      clearTimeout(saveTimer);
      saveTimer = setTimeout(saveNote, 700);
    });

    noteField.addEventListener('blur', () => {
      clearTimeout(saveTimer);
      saveNote();
    });
  }

  document.addEventListener('click', async (event) => {
    const addButton = event.target.closest('.pcard__add');
    if (addButton) {
      event.preventDefault();
      if (busy) return;
      busy = true;
      addButton.dataset.state = 'busy';
      try {
        // The card rendered from Liquid, so it knows the cap without asking.
        await add(addButton.dataset.variantId, 1, [], addButton.dataset.orderLimit || null);
        addButton.dataset.state = 'done';
        setTimeout(() => delete addButton.dataset.state, 1200);
      } catch (error) {
        delete addButton.dataset.state;
        console.error(error);
      } finally {
        busy = false;
      }
      return;
    }

    const bundleButton = event.target.closest('[data-bundle-add]');
    if (bundleButton) {
      if (busy) return;
      busy = true;
      bundleButton.disabled = true;
      try {
        const [first, ...rest] = bundleButton.dataset.bundleAdd.split(',').map(Number);
        await add(first, 1, rest.map((id) => ({ id, quantity: 1 })));
      } catch (error) {
        bundleButton.disabled = false;
        console.error(error);
      } finally {
        busy = false;
      }
      return;
    }

    if (event.target.closest('[data-cart-toggle]')) {
      event.preventDefault();
      // Open first, reconcile after. This used to await /cart.js before showing
      // anything, so tapping the cart did nothing for as long as the round trip
      // took and the drawer felt broken on a slow connection. The drawer is
      // already rendered from Liquid and kept current by every add and quantity
      // change, so what appears is right; the fetch only catches a change made
      // in another tab.
      open();
      refresh().catch(() => {});
      return;
    }

    if (event.target.closest('[data-cart-close]')) {
      event.preventDefault();
      close();
      return;
    }

    const line = event.target.closest('.cartd__line');
    if (!line || busy) return;
    const key = line.dataset.lineKey;
    const current = Number(line.querySelector('output').textContent);

    const up = event.target.closest('[data-qty-up]');
    const down = event.target.closest('[data-qty-down]');
    if (!up && !down) return;

    const wanted = up ? current + 1 : Math.max(0, current - 1);

    // The + is disabled at the cap, but a disabled button is a suggestion:
    // keyboards and assistive tech get past it. Refuse here as well, and say
    // why rather than doing nothing.
    //
    // Answered from the map rather than by re-reading the cart. A fetch here
    // would put a round trip between the tap and the number on every press,
    // which is exactly the delay the drawer was rewritten to remove.
    if (up) {
      const cap = limits.get(String(line.dataset.variantId));
      if (typeof cap === 'number' && wanted > cap) {
        say(limitNote || T('limit', 'We limit this to {n} per order.', cap));
        return;
      }
    }

    busy = true;

    // The round trip is about three quarters of a second and cannot honestly be
    // made shorter: the number has to come back from Shopify, because Shopify
    // is the one that decides whether there is stock for it. Drawing the new
    // number before it answers would be a guess, and a guess that is sometimes
    // wrong is worse than a wait.
    //
    // So the wait is shown instead of hidden. The control spins where the number
    // is, which is where the shopper is already looking.
    const stepper = line.querySelector('.cartd__qty');
    if (stepper) stepper.dataset.busy = '';

    try {
      const cart = await setQuantity(key, wanted);
      const line = cart.items.find((item) => item.key === key);

      // Shopify silently caps rather than refusing, so the shortfall has to be
      // read back and said out loud.
      if (up && line && line.quantity < wanted) {
        say(
          line.quantity > 0
            ? T('only_of_this_left', 'Only {n} of this left in stock.', line.quantity)
            : T('that_out', 'That is out of stock.')
        );
      } else {
        say('');
      }
    } catch (error) {
      // Shopify answers a refused increase with "Only 23 items were added to
      // your cart due to availability", which is about an add the shopper did
      // not make. They pressed plus; the useful fact is the ceiling.
      const ceiling = /availability|only \d+/i.test(error.detail || '');
      say(
        up && ceiling
          ? T('only_of_this_left', 'Only {n} of this left in stock.', current)
          : error.detail || T('not_updated', 'That could not be updated just now.')
      );
    } finally {
      busy = false;
      // On success the whole body has been redrawn and this element is detached,
      // which is harmless. On failure it is still on screen and has to stop
      // spinning, or the drawer sits there looking like it is still working.
      if (stepper) delete stepper.dataset.busy;
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !drawer.hidden) close();
  });

  window.StLukesCart = { add, setQuantity, clearVariant, refresh, open, close, bundles: loadBundles };

  refresh().catch(() => {
    body.innerHTML = '<p class="cartd__empty">Your cart is empty.</p>';
  });
})();
